/* ==========================================================================
   EDME — Müqxm Continuous Marquee Carousel
   Kesintisiz akış + Sonsuz döngü + Hover pause + Drag
   ========================================================================== */

(function () {
  'use strict';

  const API_NAME = 'EDMEMuqxmContinuousCarousel';
  const VERSION = '3.1.0';

  const SECTION_SELECTOR = '[data-edme-muqxm-section]';
  const TRACK_SELECTOR = '[data-edme-muqxm-track]';
  const PREV_SELECTOR = '[data-edme-muqxm-prev]';
  const NEXT_SELECTOR = '[data-edme-muqxm-next]';
  const CARD_SELECTOR = '.edme-muqxm-card';

  const DEFAULT_SPEED = 22;
  const DRAG_THRESHOLD = 8;
  const INTERACTION_DELAY = 700;

  /*
   * Aynı JavaScript dosyası birden fazla section tarafından
   * çağrılırsa tekrar event eklenmesini engeller.
   */
  if (
    window[API_NAME] &&
    window[API_NAME].version === VERSION
  ) {
    window[API_NAME].initAll();
    return;
  }

  /*
   * Tema düzenleyicisinde eski sürüm hâlâ çalışıyorsa temizle.
   */
  if (
    window[API_NAME] &&
    typeof window[API_NAME].destroyAll === 'function'
  ) {
    window[API_NAME].destroyAll();
  }

  const instances = new Map();

  function positiveModulo(value, divisor) {
    if (!divisor) return 0;

    return ((value % divisor) + divisor) % divisor;
  }

  function getTrack(section) {
    return section.querySelector(TRACK_SELECTOR);
  }

  function getPreviousButton(section) {
    return section.querySelector(PREV_SELECTOR);
  }

  function getNextButton(section) {
    return section.querySelector(NEXT_SELECTOR);
  }

  function isAutoplayEnabled(section) {
    return section.getAttribute('data-autoplay') === 'true';
  }

  function shouldPauseOnHover(section) {
    return section.getAttribute('data-pause-on-hover') !== 'false';
  }

  function getSpeed(section) {
    const speed = parseFloat(
      section.getAttribute('data-marquee-speed')
    );

    if (!Number.isFinite(speed) || speed <= 0) {
      return DEFAULT_SPEED;
    }

    return speed;
  }

  function getScrollStep(section) {
    const step = parseInt(
      section.getAttribute('data-scroll-step'),
      10
    );

    return Number.isFinite(step) && step > 0
      ? step
      : 2;
  }

  function getGap(track) {
    const styles = window.getComputedStyle(track);

    return (
      parseFloat(
        styles.columnGap ||
        styles.gap ||
        '0'
      ) || 0
    );
  }

  function getOriginalCards(track) {
    return Array.from(
      track.querySelectorAll(
        `${CARD_SELECTOR}:not([data-edme-muqxm-clone])`
      )
    );
  }

  function removeClones(track) {
    track
      .querySelectorAll('[data-edme-muqxm-clone]')
      .forEach(function (clone) {
        clone.remove();
      });
  }

  function makeClone(card) {
    const clone = card.cloneNode(true);

    clone.setAttribute(
      'data-edme-muqxm-clone',
      'true'
    );

    clone.setAttribute(
      'aria-hidden',
      'true'
    );

    clone.setAttribute(
      'tabindex',
      '-1'
    );

    clone.removeAttribute('id');

    clone.querySelectorAll('[id]').forEach(
      function (element) {
        element.removeAttribute('id');
      }
    );

    clone
      .querySelectorAll(
        'a, button, input, select, textarea, [tabindex]'
      )
      .forEach(function (element) {
        element.setAttribute('tabindex', '-1');
      });

    return clone;
  }

  function appendCloneGroup(track, cards) {
    const fragment = document.createDocumentFragment();

    cards.forEach(function (card) {
      fragment.appendChild(
        makeClone(card)
      );
    });

    track.appendChild(fragment);
  }

  function calculateLoopWidth(track, cards) {
    const gap = getGap(track);

    let totalWidth = 0;

    cards.forEach(function (card) {
      totalWidth +=
        card.getBoundingClientRect().width;
    });

    /*
     * Son orijinal kart ile ilk klon arasındaki boşluk da
     * döngü genişliğine dahildir.
     */
    totalWidth += gap * cards.length;

    return totalWidth;
  }

  function getCardDistance(track) {
    const firstCard = track.querySelector(
      `${CARD_SELECTOR}:not([data-edme-muqxm-clone])`
    );

    if (!firstCard) return 300;

    return (
      firstCard.getBoundingClientRect().width +
      getGap(track)
    );
  }

  function initSection(section) {
    if (!section || instances.has(section)) {
      return;
    }

    const track = getTrack(section);

    if (!track) return;

    const previousButton =
      getPreviousButton(section);

    const nextButton =
      getNextButton(section);

    const state = {
      section: section,
      track: track,
      previousButton: previousButton,
      nextButton: nextButton,

      loopWidth: 0,
      position: 0,

      animationFrame: null,
      lastFrameTime: 0,

      rebuildTimer: null,
      resumeTimer: null,

      hovered: false,
      focused: false,
      interacting: false,
      destroyed: false,

      pointerDown: false,
      dragging: false,
      pointerId: null,
      pointerStartX: 0,
      pointerStartScroll: 0
    };

    instances.set(section, state);

    function clearResumeTimer() {
      if (state.resumeTimer === null) {
        return;
      }

      window.clearTimeout(
        state.resumeTimer
      );

      state.resumeTimer = null;
    }

    function stopAnimation() {
      if (state.animationFrame !== null) {
        window.cancelAnimationFrame(
          state.animationFrame
        );

        state.animationFrame = null;
      }

      state.lastFrameTime = 0;
    }

    function canRun() {
      return (
        !state.destroyed &&
        isAutoplayEnabled(section) &&
        state.loopWidth > 0 &&
        !state.hovered &&
        !state.focused &&
        !state.interacting &&
        document.visibilityState === 'visible'
      );
    }

    function animationFrame(timestamp) {
      state.animationFrame = null;

      if (!canRun()) {
        state.lastFrameTime = 0;
        return;
      }

      if (!state.lastFrameTime) {
        state.lastFrameTime = timestamp;
      }

      /*
       * Sekme kısa süre donarsa carousel’in sıçramasını engeller.
       */
      const elapsed = Math.min(
        timestamp - state.lastFrameTime,
        50
      );

      state.lastFrameTime = timestamp;

      state.position +=
        getSpeed(section) *
        (elapsed / 1000);

      state.position = positiveModulo(
        state.position,
        state.loopWidth
      );

      track.scrollLeft = state.position;

      state.animationFrame =
        window.requestAnimationFrame(
          animationFrame
        );
    }

    function startAnimation() {
      if (
        !canRun() ||
        state.animationFrame !== null
      ) {
        return;
      }

      state.lastFrameTime = 0;

      state.animationFrame =
        window.requestAnimationFrame(
          animationFrame
        );
    }

    function syncAnimation() {
      if (canRun()) {
        startAnimation();
      } else {
        stopAnimation();
      }
    }

    function pauseForInteraction() {
      clearResumeTimer();

      state.interacting = true;

      stopAnimation();
    }

    function resumeAfterInteraction(delay) {
      clearResumeTimer();

      state.resumeTimer =
        window.setTimeout(function () {
          state.resumeTimer = null;
          state.interacting = false;

          state.position = positiveModulo(
            track.scrollLeft,
            state.loopWidth
          );

          track.scrollLeft =
            state.position;

          syncAnimation();
        }, delay || INTERACTION_DELAY);
    }

    function updateArrowStates() {
      const disabled =
        state.loopWidth <= 0;

      if (previousButton) {
        previousButton.disabled = disabled;

        previousButton.classList.toggle(
          'is-disabled',
          disabled
        );
      }

      if (nextButton) {
        nextButton.disabled = disabled;

        nextButton.classList.toggle(
          'is-disabled',
          disabled
        );
      }
    }

    function buildInfiniteLoop() {
      stopAnimation();
      removeClones(track);

      track.classList.toggle(
        'is-marquee',
        isAutoplayEnabled(section)
      );

      const originalCards =
        getOriginalCards(track);

      if (!originalCards.length) {
        state.loopWidth = 0;
        updateArrowStates();
        return;
      }

      const loopWidth =
        calculateLoopWidth(
          track,
          originalCards
        );

      if (
        !Number.isFinite(loopWidth) ||
        loopWidth <= 1
      ) {
        state.loopWidth = 0;
        updateArrowStates();
        return;
      }

      state.loopWidth = loopWidth;

      /*
       * En az bir tam kopya gerekir.
       */
      appendCloneGroup(
        track,
        originalCards
      );

      /*
       * Orijinal görsel sayısı azsa ekranın sağ tarafında
       * boşluk kalmaması için ek gruplar oluşturulur.
       */
      let safetyCounter = 0;

      while (
        track.scrollWidth <
          track.clientWidth +
          loopWidth +
          50 &&
        safetyCounter < 20
      ) {
        appendCloneGroup(
          track,
          originalCards
        );

        safetyCounter += 1;
      }

      state.position = positiveModulo(
        state.position,
        loopWidth
      );

      track.scrollLeft =
        state.position;

      updateArrowStates();
      syncAnimation();
    }

    function scheduleRebuild() {
      if (state.rebuildTimer !== null) {
        window.clearTimeout(
          state.rebuildTimer
        );
      }

      state.rebuildTimer =
        window.setTimeout(function () {
          state.rebuildTimer = null;
          buildInfiniteLoop();
        }, 120);
    }

    function handleMouseEnter() {
      if (!shouldPauseOnHover(section)) {
        return;
      }

      state.hovered = true;
      stopAnimation();
    }

    function handleMouseLeave() {
      if (!shouldPauseOnHover(section)) {
        return;
      }

      state.hovered = false;
      syncAnimation();
    }

    function handleFocusIn() {
      state.focused = true;
      stopAnimation();
    }

    function handleFocusOut() {
      window.setTimeout(function () {
        state.focused = track.contains(
          document.activeElement
        );

        syncAnimation();
      }, 0);
    }

    function handlePointerDown(event) {
      if (
        typeof event.button === 'number' &&
        event.button !== 0
      ) {
        return;
      }

      pauseForInteraction();

      state.pointerDown = true;
      state.dragging = false;
      state.pointerId = event.pointerId;
      state.pointerStartX = event.clientX;
      state.pointerStartScroll = track.scrollLeft;
    }

    function handlePointerMove(event) {
      if (!state.pointerDown) return;

      /*
       * Mobilde doğal parmak kaydırması kullanılır.
       */
      if (event.pointerType === 'touch') {
        return;
      }

      const difference =
        event.clientX -
        state.pointerStartX;

      if (
        !state.dragging &&
        Math.abs(difference) <
          DRAG_THRESHOLD
      ) {
        return;
      }

      if (!state.dragging) {
        state.dragging = true;

        /*
         * Sonsuz döngünün başına yakınken yalnızca gerçek sürükleme
         * başladığında klon grubuna geçilir. Normal tıklamada scrollLeft
         * değiştirilmediği için ürün bağlantısı sorunsuz açılır.
         */
        if (
          state.loopWidth > 0 &&
          track.scrollLeft <
            getCardDistance(track) * 2
        ) {
          track.scrollLeft += state.loopWidth;
          state.pointerStartScroll += state.loopWidth;
        }

        track.classList.add(
          'is-dragging'
        );

        if (
          typeof track.setPointerCapture ===
            'function' &&
          state.pointerId !== null
        ) {
          try {
            track.setPointerCapture(
              state.pointerId
            );
          } catch (error) {}
        }
      }

      event.preventDefault();

      track.scrollLeft =
        state.pointerStartScroll -
        difference;
    }

    function finishPointer() {
      if (!state.pointerDown) return;

      const wasDragging = state.dragging;

      state.pointerDown = false;
      state.dragging = false;

      if (wasDragging) {
        track.dataset.wasDragged = 'true';

        /*
         * click olayı pointerup sonrasında çalıştığı için bayrağı bir
         * sonraki event döngüsünde kaldırıyoruz. Böylece yalnızca
         * sürükleme sonrasındaki yanlış tıklama iptal edilir.
         */
        window.setTimeout(function () {
          delete track.dataset.wasDragged;
        }, 0);
      }

      track.classList.remove(
        'is-dragging'
      );

      if (
        typeof track.releasePointerCapture ===
          'function' &&
        state.pointerId !== null
      ) {
        try {
          track.releasePointerCapture(
            state.pointerId
          );
        } catch (error) {}
      }

      state.pointerId = null;

      /*
       * Normal tıklamada scrollLeft değerine dokunma. Aksi hâlde anchor
       * üzerindeki pointerdown/pointerup hedefi değişebilir ve href açılmaz.
       */
      if (wasDragging && state.loopWidth > 0) {
        state.position = positiveModulo(
          track.scrollLeft,
          state.loopWidth
        );

        track.scrollLeft = state.position;
      } else {
        state.position = positiveModulo(
          track.scrollLeft,
          state.loopWidth
        );
      }

      resumeAfterInteraction();
    }

    function preventClickAfterDrag(event) {
      if (
        track.dataset.wasDragged !==
        'true'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    }

    function handleNativeScroll() {
      if (!state.interacting) return;

      state.position = positiveModulo(
        track.scrollLeft,
        state.loopWidth
      );

      /*
       * Mobil momentum kaydırması devam ettiği sürece
       * otomatik akışın başlamasını geciktirir.
       */
      resumeAfterInteraction();
    }

    function handleWheel() {
      pauseForInteraction();
      resumeAfterInteraction();
    }

    function scrollPrevious() {
      if (state.loopWidth <= 0) return;

      pauseForInteraction();

      const amount =
        getCardDistance(track) *
        getScrollStep(section);

      let currentPosition =
        track.scrollLeft;

      if (currentPosition < amount) {
        currentPosition +=
          state.loopWidth;

        track.scrollLeft =
          currentPosition;
      }

      track.scrollTo({
        left:
          currentPosition -
          amount,
        behavior: 'smooth'
      });

      resumeAfterInteraction(600);
    }

    function scrollNext() {
      if (state.loopWidth <= 0) return;

      pauseForInteraction();

      const amount =
        getCardDistance(track) *
        getScrollStep(section);

      track.scrollTo({
        left:
          track.scrollLeft +
          amount,
        behavior: 'smooth'
      });

      resumeAfterInteraction(600);
    }

    function handleResize() {
      scheduleRebuild();
    }

    function handleVisibilityChange() {
      syncAnimation();
    }

    track.addEventListener(
      'mouseenter',
      handleMouseEnter
    );

    track.addEventListener(
      'mouseleave',
      handleMouseLeave
    );

    track.addEventListener(
      'focusin',
      handleFocusIn
    );

    track.addEventListener(
      'focusout',
      handleFocusOut
    );

    track.addEventListener(
      'pointerdown',
      handlePointerDown
    );

    track.addEventListener(
      'pointermove',
      handlePointerMove
    );

    track.addEventListener(
      'pointerup',
      finishPointer
    );

    track.addEventListener(
      'pointercancel',
      finishPointer
    );

    track.addEventListener(
      'scroll',
      handleNativeScroll,
      {
        passive: true
      }
    );

    track.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: true
      }
    );

    track.addEventListener(
      'click',
      preventClickAfterDrag,
      true
    );

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        scrollPrevious
      );
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        scrollNext
      );
    }

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true
      }
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    state.destroy = function () {
      state.destroyed = true;

      stopAnimation();
      clearResumeTimer();

      if (state.rebuildTimer !== null) {
        window.clearTimeout(
          state.rebuildTimer
        );
      }

      window.removeEventListener(
        'resize',
        handleResize
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      removeClones(track);

      instances.delete(section);
    };

    /*
     * İlk ölçüm.
     */
    window.requestAnimationFrame(
      buildInfiniteLoop
    );

    /*
     * Tema CSS’i veya görseller geç yüklenirse ölçümü yeniler.
     */
    window.setTimeout(
      buildInfiniteLoop,
      250
    );

    window.setTimeout(
      buildInfiniteLoop,
      800
    );
  }

  function initAll() {
    document
      .querySelectorAll(SECTION_SELECTOR)
      .forEach(initSection);
  }

  function destroyAll() {
    Array.from(
      instances.values()
    ).forEach(function (state) {
      if (
        typeof state.destroy ===
        'function'
      ) {
        state.destroy();
      }
    });
  }

  window[API_NAME] = {
    version: VERSION,
    initAll: initAll,
    destroyAll: destroyAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAll
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      if (
        event.target.matches &&
        event.target.matches(
          SECTION_SELECTOR
        )
      ) {
        initSection(event.target);
        return;
      }

      event.target
        .querySelectorAll(
          SECTION_SELECTOR
        )
        .forEach(initSection);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      const sections = [];

      if (
        event.target.matches &&
        event.target.matches(
          SECTION_SELECTOR
        )
      ) {
        sections.push(event.target);
      }

      event.target
        .querySelectorAll(
          SECTION_SELECTOR
        )
        .forEach(function (section) {
          sections.push(section);
        });

      sections.forEach(function (section) {
        const state =
          instances.get(section);

        if (
          state &&
          typeof state.destroy ===
          'function'
        ) {
          state.destroy();
        }
      });
    }
  );
})();